// Template ID Mapping
export const TEMPLATE_ID_MAP: Record<string, number> = {
  payment: 4,
  purchase: 3,
  invoice: 1,
  sales: 1,
  reminder: 2,
  debit: 5,
  credit: 6,
  receipt: 2,
  customer: 7,
  supplier: 8,
  pos: 1,
};

export const DEFAULT_TEMPLATE_ID = 1;

// Modal Headers
export const MODAL_HEADERS: Record<string, string> = {
  customer: 'Send Transactional Email to Customers',
  supplier: 'Send Transactional Email to Suppliers',
  salesInvoice: 'Send Invoice Email',
  purchaseInvoice: 'Send Purchase Invoice Email',
  receipt: 'Send Receipt Email',
  payment: 'Send Payment Confirmation Email',
  creditNote: 'Send Credit Note Email',
  debitNote: 'Send Debit Note Email',
  pos: 'Send POS Invoice Email',
};

// Email Type Mapping
export const TYPE_MAPPING: Record<string, string> = {
  customer: 'transaction',
  supplier: 'transaction',
  salesinvoice: 'invoice',
  purchaseinvoice: 'purchase',
  receipt: 'receipt',
  payment: 'paymentconfirm',
  creditnote: 'creditnote',
  debitnote: 'debitnote',
};

// UI Constants
export const UI_CONSTANTS = {
  TEXTAREA_MIN_ROWS: 14,
  TEXTAREA_MAX_ROWS: 26,
  ACCORDION_MAX_HEIGHT: '180px',
  PLACEHOLDER_MAX_HEIGHT: '140px',
  RESET_TIMEOUT: 100,
  INITIAL_PROGRESS: {
    sentEmails: 0,
    totalEmails: 0,
    message: 'Preparing to send emails...',
    progressPercentage: 0,
  },
} as const;

// Color Constants
export const COLORS = {
  PRIMARY_GRADIENT: 'linear-gradient(135deg, #911B7C 0%, #6B1460 100%)',
  PRIMARY_HOVER_GRADIENT: 'linear-gradient(135deg, #6B1460 0%, #911B7C 100%)',
  SUCCESS_GRADIENT: 'linear-gradient(135deg, #2e7d32 0%, #1b5e20 100%)',
  SUCCESS_HOVER_GRADIENT: 'linear-gradient(135deg, #388e3c 0%, #2e7d32 100%)',
  PRIMARY_COLOR: '#911B7C',
  PRIMARY_LIGHT: 'rgba(145, 27, 124, 0.1)',
  PRIMARY_BACKGROUND: 'rgba(145, 27, 124, 0.04)',
} as const;

// Messages
export const MESSAGES = {
  SUCCESS: {
    SAVE: 'Email template saved successfully!',
    UPDATE: 'Email template updated successfully!',
    CANCEL: 'Template update cancelled',
  },
  ERROR: {
    SAVE_FAILED: 'Failed to save template changes',
  },
  WARNING: {
    NO_CHANGES: 'No changes detected. Please modify the template content before saving.',
    TEMPLATE_DISCARDED: 'Template changes have been discarded. The original template content will be used.',
  },
  INFO: {
    PLACEHOLDER_INSTRUCTION: 'Click on any placeholder to copy it to your clipboard:',
    PLACEHOLDER_DESCRIPTION: 'These placeholders will be automatically replaced with actual values when sending emails. Make sure to use the exact placeholder format shown above.',
    UNSAVED_CHANGES_NEXT: 'You have unsaved changes. They will be saved automatically when you proceed.',
    UNSAVED_CHANGES_SEND: 'You have unsaved changes. Please save the template or choose an option before sending.',
    THIS_EMAIL_ONLY: 'Template content will be used for this email only',
  },
} as const;

// Email Placeholders
export interface PlaceholderItem {
  name: string;
  description: string;
}

export const EMAIL_PLACEHOLDERS: PlaceholderItem[] = [
  { name: '[Customer Name]', description: 'Customer or party name' },
  { name: '[Supplier Name]', description: 'Supplier or vendor name' },
  { name: '[partyName]', description: 'Party name' },
  { name: '[Invoice Number]', description: 'Invoice number' },
  { name: '[Invoice Date]', description: 'Invoice date' },
  { name: '[Purchase Order Number]', description: 'Purchase order number' },
  { name: '[Purchase Date]', description: 'Purchase date' },
  { name: '[Due Date]', description: 'Payment due date' },
  { name: '[Amount]', description: 'Invoice amount' },
  { name: '[debitNoteNumber]', description: 'Debit note number' },
  { name: '[debitNoteDate]', description: 'Debit note date' },
  { name: '[creditNoteNumber]', description: 'Credit note number' },
  { name: '[creditNoteDate]', description: 'Credit note date' },
  { name: '[Date From]', description: 'Start date for transaction summary' },
  { name: '[Date To]', description: 'End date for transaction summary' },
  { name: '[As of Date]', description: 'As of date for outstanding balance' },
  { name: '[Currency]', description: 'Currency symbol or code' },
  { name: '[Outstanding Amount]', description: 'Outstanding balance amount' },
  { name: '[Payment Link]', description: 'Payment link URL' },
  { name: '[Company Name]', description: 'Your company name' },
  { name: '[Company Email]', description: 'Your company email' },
  { name: '[Company Phone No]', description: 'Your company phone number' },
  { name: '[companyName]', description: 'Company name' },
  { name: '[companyEmail]', description: 'Company email' },
  { name: '[companyPhone]', description: 'Company phone' },
  { name: '[Your Name]', description: 'Your name' },
  { name: '[Your Position]', description: 'Your position/title' },
  { name: '[Email Address]', description: 'Your email address' },
  { name: '[Phone Number]', description: 'Your phone number' },
  { name: '[Payment Method]', description: 'Payment method' },
  { name: '[Payment Method(s)]', description: 'Available payment methods' },
  { name: '[Payment Amount]', description: 'Payment amount received' },
  { name: '[Payment Date]', description: 'Date of payment' },
  { name: '[Transaction ID]', description: 'Payment transaction ID' },
  { name: '[Reference Number]', description: 'Payment reference number' },
  { name: '[Website URL]', description: 'Company website' },
];

// Helper Functions
export const getTemplateIdByPageType = (pageType: string): number => {
  const pageTypeLower = pageType.toLowerCase();
  for (const [key, id] of Object.entries(TEMPLATE_ID_MAP)) {
    if (pageTypeLower.includes(key)) {
      return id;
    }
  }
  return DEFAULT_TEMPLATE_ID;
};

